// src/test-client/TestDashboard.jsx
import React, { useEffect, useState, useCallback } from "react";
import { LineChart, Line, Tooltip, ResponsiveContainer } from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import {
  HomeIcon,
  ClipboardListIcon,
  SearchIcon,
  User,
  BookOpenIcon,
  SparklesIcon,
  MessageSquare,
  MenuIcon,
  MoreHorizontal,
  ChevronDown,
  ChevronUp,
  FileText,
  UsersIcon,
  Briefcase,
  StarIcon,
  Phone,
  Video,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import DashboardCalendar from "../therapist/test-therapist/DashboardCalendar";
import { useAuth } from "../lib/AuthContext";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { parseISO, isToday, isTomorrow, format } from "date-fns";
import { Button } from "../components/ui/button";
import Notifications from "../components/Notifications";
import ClientProfile from "../client/ClientProfile";
import ClientBookings from "../client/ClientBookings";
import ClientResources from "../client/ClientResources";
import ClientWhatsNew from "../client/ClientWhatsNew";
import ClientFindTherapist from "../client/ClientFindTherapist";
import DirectMessages from "../common/DirectMessages";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

const fadeLeft = {
  hidden: { opacity: 0, x: -20 },
  show: { opacity: 1, x: 0, transition: { duration: 0.4 } }
};
const fadeRight = {
  hidden: { opacity: 0, x: 20 },
  show: { opacity: 1, x: 0, transition: { duration: 0.4 } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.5 } }
};

const SidebarItem = ({
  icon: Icon,
  label,
  to,
  activeContent,
  setActiveContent,
  isSidebarExpanded,
}) => (
  <button
    onClick={() => setActiveContent(to)}
    className={`flex items-center p-2 rounded-lg w-full text-sm transition-colors ${activeContent === to
      ? "bg-green-100 text-green-700"
      : "text-gray-600 hover:bg-gray-50"
      }`}
  >
    <Icon className="h-5 w-5" />
    {isSidebarExpanded && <span className="ml-3">{label}</span>}
  </button>
);

const TestDashboard = () => {
  const { accessToken, user, setUser, logout } = useAuth();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [importantBooking, setImportantBooking] = useState(null);
  const [bookingVersion, setBookingVersion] = useState(0);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeContent, setActiveContent] = useState("home");
  const [tips, setTips] = useState([]);
  const [quote, setQuote] = useState({ text: "", author: "" });

  const sidebarItems = [
    { name: "home", icon: HomeIcon, label: "Home" },
    { name: "bookings", icon: ClipboardListIcon, label: "Bookings" },
    { name: "find-therapist", icon: SearchIcon, label: "Find Therapist" },
    { name: "profile", icon: User, label: "Profile" },
    { name: "resources", icon: BookOpenIcon, label: "Resources" },
    { name: "whats-new", icon: SparklesIcon, label: "What's New" },
    { name: "messages", icon: MessageSquare, label: "Messages" },
  ];

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axios.get(
        `https://quirkleai.com/api/bookings/?user_type=client&status=upcoming`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      const now = new Date();
      const formatted = res.data
        .map((b) => {
          const start = parseISO(b.start_time);
          const end = parseISO(b.end_time);
          return {
            ...b,
            isLive: now >= start && now <= end,
          };
        })
        .sort((a, b) => parseISO(a.start_time) - parseISO(b.start_time));

      setImportantBooking(formatted[0] || null);
      setBookings(formatted.slice(0, 3));
    } catch (err) {
      toast.error("Failed to load bookings.");
    }
  }, [accessToken]);

  useEffect(() => {
    fetchBookings();
    axios.get("https://quirkleai.com/api/wellness-tips/").then((res) => setTips(res.data));
    axios.get("https://quirkleai.com/api/motivation/").then((res) => setQuote(res.data));
  }, [fetchBookings]);

  useEffect(() => {
    console.log("%c🔥 TestDashboard Mounted", "background:#0ea5a4;color:white;padding:6px");
  }, []);

  const handleStartNow = async (id, meetingId) => {
    try {
      await axios.patch(
        `https://quirkleai.com/api/sessions/${id}/activate/`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      navigate(`/client/jitsi-room/${meetingId}`);
    } catch (err) {
      toast.error("Failed to activate meeting.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`https://quirkleai.com/api/bookings/${id}/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      toast.success("Booking cancelled.");
      setBookingVersion((v) => v + 1);
    } catch {
      toast.error("Failed to cancel booking.");
    }
  };

  const getRelativeTimeInfo = (b) => {
    if (!b) return "";
    const start = parseISO(b.start_time);
    if (b.isLive) return "Now";
    if (isToday(start)) return "Today";
    if (isTomorrow(start)) return "Tomorrow";
    return format(start, "MMM d");
  };

  const monthlyVisitorsData = [
    { name: "Week 1", value: 5 },
    { name: "Week 2", value: 8 },
    { name: "Week 3", value: 6 },
    { name: "Week 4", value: 10 },
  ];

  return (
    <div className="flex h-screen bg-[#F7F8FA]">

      {/* DESKTOP SIDEBAR */}
      <motion.aside
        initial={false}
        animate={{
          width: isSidebarExpanded ? 260 : 80,
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="hidden md:flex flex-col bg-white py-4 shadow-md h-screen"
      >
        <div className="flex justify-start px-2 mb-4">
          <button
            onClick={() => setIsSidebarExpanded((s) => !s)}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
          >
            <MenuIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 space-y-2">
          {sidebarItems.map((item) => (
            <motion.div
              key={item.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <SidebarItem
                icon={item.icon}
                label={item.label}
                to={item.name}
                activeContent={activeContent}
                setActiveContent={setActiveContent}
                isSidebarExpanded={isSidebarExpanded}
              />
            </motion.div>
          ))}
        </div>

        <div className="mt-auto px-2 pb-4">
          <button
            onClick={logout}
            className="flex items-center p-2 text-gray-600 hover:bg-gray-50 rounded-lg w-full"
          >
            🚪 {isSidebarExpanded && "Logout"}
          </button>
        </div>
      </motion.aside>

      {/* MAIN CONTENT */}
      <main
        className={`flex-1 grid grid-cols-12 gap-6 p-4 md:p-6 overflow-y-auto transition-all duration-300 ${isSidebarExpanded ? "md:ml-72" : "md:ml-20"
          }`}
      >
        {/* HEADER */}
        <div className="col-span-12">
          <motion.header
            className="flex items-center justify-between"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >


            {/* LEFT — TITLE + MOBILE MENU */}
            <div className="flex items-center gap-4">

              <button
                className="md:hidden p-2 rounded-lg bg-white shadow-sm"
                onClick={() => setMobileMenuOpen((p) => !p)}
              >
                <MenuIcon className="w-5 h-5 text-gray-700" />
              </button>

              <div>
                <h1 className="text-3xl font-bold text-gray-800">Client Dashboard</h1>
                <p className="text-gray-500">
                  Welcome, {user?.first_name || user?.username || "Client"}!
                </p>
              </div>
            </div>

            {/* RIGHT — SEARCH + NOTIFS (WITH PROFESSIONAL MEDIUM GAP) */}
            <div className="flex items-center gap-6">

              {/* Search (Desktop) */}
              <div className="relative hidden sm:block">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 rounded-lg bg-white border focus:ring-2 focus:ring-green-400 w-56"
                />
              </div>

              {/* Mobile Search */}
              <MobileSearch />

              {/* Notifications */}
              <Notifications accessToken={accessToken} />
            </div>
          </motion.header>

          <AnimatePresence>
            {mobileMenuOpen && (
              <>
                {/* Backdrop */}
                <motion.div
                  className="fixed inset-0 bg-black/40 z-40"
                  onClick={() => setMobileMenuOpen(false)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                />

                {/* Slide Sidebar */}
                <motion.div
                  className="fixed left-0 top-0 h-full w-72 bg-white shadow-xl z-50 p-4 flex flex-col"
                  initial={{ x: -300 }}
                  animate={{ x: 0 }}
                  exit={{ x: -300 }}
                  transition={{ type: "tween", duration: 0.35 }}
                >
                  {/* Top Row */}
                  <div className="flex items-center justify-between mb-4">
                    <motion.h2
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1 }}
                      className="text-lg font-semibold"
                    >
                      Menu
                    </motion.h2>
                    <button
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 text-gray-700"
                    >
                      ✖
                    </button>
                  </div>

                  {/* Nav */}
                  <motion.nav
                    className="flex-1 overflow-y-auto space-y-2"
                    initial="hidden"
                    animate="show"
                    variants={{
                      hidden: {},
                      show: { transition: { staggerChildren: 0.12 } },
                    }}
                  >
                    {sidebarItems.map((item) => (
                      <motion.button
                        key={item.name}
                        onClick={() => {
                          setActiveContent(item.name);
                          setMobileMenuOpen(false);
                        }}
                        variants={{
                          hidden: { opacity: 0, x: -20 },
                          show: { opacity: 1, x: 0 },
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 text-gray-700 rounded-lg"
                      >
                        <item.icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </motion.button>
                    ))}
                  </motion.nav>

                  {/* Logout */}
                  <motion.button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    variants={fadeUp}
                    className="w-full text-left px-4 py-3 text-red-600 border-t mt-2"
                  >
                    🚪 Logout
                  </motion.button>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* ================= HOME CONTENT ================= */}
        {activeContent === "home" && (
          <>
            {/* LEFT COLUMN */}
            <div className="col-span-12 lg:col-span-8 space-y-6">

              {/* Upcoming Appointments */}
              <Card className="shadow-sm rounded-xl">
                <CardHeader className="flex justify-between items-center">
                  <CardTitle>Upcoming Appointments</CardTitle>
                  <MoreHorizontal className="text-gray-400" />
                </CardHeader>

                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px]">
                      <thead>
                        <tr className="text-left text-gray-400 text-sm">
                          <th className="pb-3">Therapist</th>
                          <th className="pb-3">Date</th>
                          <th className="pb-3">Time</th>
                          <th className="pb-3">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings.map((b) => (
                          <tr key={b.id} className="border-t border-gray-100">
                            <td className="py-3 flex items-center">
                              <img
                                src={`https://i.pravatar.cc/40?u=${b.therapist.username}`}
                                className="rounded-full mr-3"
                              />
                              <p className="font-semibold text-gray-700">{b.therapist.username}</p>
                            </td>

                            <td className="py-3 text-gray-600">
                              {new Date(b.start_time).toLocaleDateString()}
                            </td>

                            <td className="py-3 text-gray-600">
                              {new Date(b.start_time).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </td>

                            <td className="py-3">
                              {b.isLive ? (
                                <Button
                                  className="bg-green-500 text-white hover:bg-green-600 px-4 py-2 rounded-lg"
                                  onClick={() => handleStartNow(b.id, b.meeting_id)}
                                >
                                  Start Now
                                </Button>
                              ) : (
                                <Button
                                  className="bg-red-500 text-white hover:bg-red-600 px-4 py-2 rounded-lg"
                                  onClick={() => handleDelete(b.id)}
                                >
                                  Cancel
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Calendar */}
              <Card className="shadow-sm rounded-xl">
                <CardHeader className="flex justify-between items-center">
                  <CardTitle>Your Schedule</CardTitle>
                  <MoreHorizontal className="text-gray-400" />
                </CardHeader>

                <CardContent>
                  <DashboardCalendar
                    bookingVersion={bookingVersion}
                    onBookingAdded={() => setBookingVersion((v) => v + 1)}
                  />
                </CardContent>
              </Card>
            </div>

            {/* RIGHT COLUMN */}
            <div className="col-span-12 lg:col-span-4 space-y-6">
              <Card className="bg-[#4E4E8D] text-white rounded-xl shadow-lg">
                <CardHeader className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <img
                      src={
                        user?.profile_picture
                          ? `http://localhost:8000${user.profile_picture}`
                          : "https://i.pravatar.cc/40?u=random"
                      }
                      className="rounded-full border-2 border-white"
                      style={{ width: 40, height: 40 }}
                    />
                    <div>
                      <CardTitle className="text-base font-bold">
                        {user?.first_name} {user?.last_name}
                      </CardTitle>
                      <p className="text-xs opacity-80">{user?.headline || "Client"}</p>
                    </div>
                  </div>
                  <MoreHorizontal className="cursor-pointer" onClick={() => setShowProfileModal(true)} />
                </CardHeader>

                <CardContent>
                  <div className="bg-[#6A6AB4] p-4 rounded-lg">
                    <p className="text-sm font-semibold">Next Session</p>
                    <p className="text-xl font-bold">
                      {importantBooking
                        ? `with ${importantBooking.therapist.username}`
                        : "No upcoming sessions"}
                    </p>

                    <div className="flex justify-between mt-3 text-sm">
                      <div>
                        <p className="opacity-80">Date</p>
                        <p className="font-semibold">
                          {importantBooking
                            ? format(parseISO(importantBooking.start_time), "MMM d, yyyy")
                            : "N/A"}
                        </p>
                      </div>

                      <div>
                        <p className="opacity-80">Time</p>
                        <p className="font-semibold">
                          {importantBooking
                            ? format(parseISO(importantBooking.start_time), "p")
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex mt-4 space-x-2">
                    <button
                      className="flex-1 bg-white text-[#4E4E8D] font-semibold py-2.5 rounded-lg"
                      onClick={() => setActiveContent("find-therapist")}
                    >
                      Find a Therapist
                    </button>

                    <button className="p-2.5 bg-white/20 rounded-lg">
                      <Phone className="w-5 h-5" />
                    </button>

                    <button className="p-2.5 bg-white/20 rounded-lg">
                      <Video className="w-5 h-5" />
                    </button>
                  </div>
                </CardContent>
              </Card>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <StatCard value="12" label="Therapists" icon={<UsersIcon className="text-red-500" />} />
                <StatCard value="5" label="Sessions" icon={<FileText className="text-blue-500" />} />
                <StatCard value="3" label="Completed" icon={<Briefcase className="text-yellow-500" />} />
                <StatCard value="4.8" label="Rating" icon={<StarIcon className="text-green-500" />} />
              </div>

              <Card className="rounded-xl shadow-sm">
                <CardHeader className="flex justify-between items-center">
                  <CardTitle>Your Activity</CardTitle>
                  <button className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                    1 Week <ChevronDown className="w-4 h-4 ml-1" />
                  </button>
                </CardHeader>

                <CardContent>
                  <ResponsiveContainer width="100%" height={100}>
                    <LineChart data={monthlyVisitorsData}>
                      <Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} />
                      <Tooltip />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="rounded-xl shadow-sm">
                <CardHeader className="flex justify-between items-center">
                  <CardTitle>Important Schedule</CardTitle>
                  <MoreHorizontal className="text-gray-400" />
                </CardHeader>

                <CardContent>
                  {importantBooking ? (
                    <>
                      <div className="flex items-center gap-3">
                        <img
                          src={`https://i.pravatar.cc/40?u=${importantBooking.therapist.username}`}
                          className="rounded-full"
                        />
                        <div>
                          <p className="font-semibold text-gray-700">
                            Session with {importantBooking.therapist.username}
                          </p>
                          <p className="text-sm text-gray-500">
                            {getRelativeTimeInfo(importantBooking)} |{" "}
                            {format(parseISO(importantBooking.start_time), "p")}
                          </p>
                        </div>
                      </div>

                      <button
                        className="w-full bg-green-500 text-white rounded-lg font-semibold py-2.5 mt-4"
                        onClick={() =>
                          importantBooking.isLive
                            ? handleStartNow(importantBooking.id, importantBooking.meeting_id)
                            : toast.info("You can only start at the scheduled time.")
                        }
                      >
                        Start Call
                      </button>
                    </>
                  ) : (
                    <p className="text-gray-500 text-center">No upcoming appointments.</p>
                  )}
                </CardContent>
              </Card>

              {/* Latest Notification */}
              <Card className="rounded-xl shadow-sm">
                <CardHeader className="flex justify-between items-center">
                  <CardTitle>Latest Notification</CardTitle>
                  <MoreHorizontal className="text-gray-400" />
                </CardHeader>

                <CardContent>
                  <p className="font-semibold text-gray-700">New resource available!</p>
                  <p className="text-sm text-gray-500">
                    Check out the new article on mindfulness.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Tips */}
            <div className="col-span-12 space-y-6">
              <Card className="rounded-xl shadow-sm">
                <CardHeader>
                  <CardTitle>Daily Wellness Tips</CardTitle>
                </CardHeader>

                <CardContent className="grid sm:grid-cols-2 gap-4 text-sm">
                  {tips.length ? (
                    tips.map((t, i) => (
                      <div key={i} className="p-3 bg-green-100 rounded-md">
                        <p className="font-semibold text-green-800">{t.title}</p>
                        <p className="text-gray-600">{t.content}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No tips available today.</p>
                  )}
                </CardContent>
              </Card>

              {/* Quote */}
              <Card className="rounded-xl shadow-sm">
                <CardContent className="pt-6 text-center text-gray-700 font-medium">
                  {quote.text ? (
                    <>
                      <p className="italic">"{quote.text}"</p>
                      <p className="mt-1 text-sm text-gray-500">– {quote.author}</p>
                    </>
                  ) : (
                    <p className="text-gray-500">No quote available.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* OTHER TABS */}
        {activeContent === "messages" && (
          <div className="col-span-12">
            <DirectMessages />
          </div>
        )}

        {activeContent === "profile" && (
          <div className="col-span-12">
            <ClientProfile onCancel={() => setShowProfileModal(false)} />
          </div>
        )}

        {activeContent === "bookings" && (
          <div className="col-span-12">
            <ClientBookings />
          </div>
        )}

        {activeContent === "find-therapist" && (
          <div className="col-span-12">
            <ClientFindTherapist />
          </div>
        )}

        {activeContent === "resources" && (
          <div className="col-span-12">
            <ClientResources />
          </div>
        )}

        {activeContent === "whats-new" && (
          <div className="col-span-12">
            <ClientWhatsNew />
          </div>
        )}
      </main>

      {/* PROFILE MODAL */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl mt-16 shadow-lg overflow-auto">
            <ClientProfile onCancel={() => setShowProfileModal(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default TestDashboard;

/* ---------- MOBILE SEARCH COMPONENT ---------- */
function MobileSearch() {
  const [open, setOpen] = useState(false);
  return (
    <div className="sm:hidden relative">
      {!open ? (
        <button className="p-2 rounded-lg bg-white shadow-sm" onClick={() => setOpen(true)}>
          <SearchIcon className="w-5 h-5 text-gray-600" />
        </button>
      ) : (
        <div className="absolute right-0 top-0 z-50">
          <div className="flex items-center gap-2 bg-white rounded-md shadow px-2 py-1">
            <input autoFocus placeholder="Search..." className="outline-none px-2" />
            <button className="p-1 text-gray-500" onClick={() => setOpen(false)}>
              ✖
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Simple Stat Card ---------- */
function StatCard({ value, label, icon }) {
  return (
    <Card className="rounded-xl shadow-sm">
      <CardContent className="pt-4 flex items-center space-x-3">
        <div className="p-2 bg-gray-100 rounded-lg">{icon}</div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-gray-400">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
